import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Calendar, ArrowLeft, Clock, CheckCircle, BookOpen,
  FileText, Brain, PenTool, RefreshCw, Target, Loader2,
  Lightbulb, AlertTriangle
} from 'lucide-react';

import { getApiBaseUrl } from '../services/api';
const API_URL = getApiBaseUrl();

const TASK_TYPE_CONFIG = {
  ncert: { icon: BookOpen, color: 'emerald', label: 'NCERT Reading' },
  editorial: { icon: FileText, color: 'blue', label: 'Editorial' },
  quiz: { icon: Target, color: 'orange', label: 'Quiz' },
  mcq: { icon: Brain, color: 'purple', label: 'MCQ Practice' },
  answer: { icon: PenTool, color: 'cyan', label: 'Answer Writing' },
  revision: { icon: RefreshCw, color: 'yellow', label: 'Revision' },
  test: { icon: Target, color: 'red', label: 'Test' },
  study: { icon: BookOpen, color: 'indigo', label: 'Deep Study' },
  pyq: { icon: FileText, color: 'pink', label: 'PYQ Analysis' },
  planning: { icon: Calendar, color: 'slate', label: 'Planning' },
  rest: { icon: Lightbulb, color: 'green', label: 'Light/Rest' },
  weakness: { icon: AlertTriangle, color: 'orange', label: 'Weak Area Focus' }
};

const WeeklyPlannerPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [completedTasks, setCompletedTasks] = useState({});
  const [selectedDay, setSelectedDay] = useState(getCurrentDay());

  function getCurrentDay() {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  }

  useEffect(() => {
    if (!user || !token) {
      navigate('/auth');
      return;
    }
    fetchWeeklyPlan();
    loadCompletedTasks();
  }, [user, token, navigate]);

  const fetchWeeklyPlan = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/roadmap/weekly-plan`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        if (res.status === 404) {
          toast.error('Please select a year track first');
          navigate('/roadmap');
          return;
        }
        throw new Error('Failed to fetch');
      }
      
      const data = await res.json();
      setWeeklyPlan(data);
    } catch (error) {
      console.error('Error fetching weekly plan:', error);
      toast.error('Failed to load weekly plan');
    } finally {
      setLoading(false);
    }
  };

  const loadCompletedTasks = () => {
    const saved = localStorage.getItem('weekly_completed_tasks');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Only use if same week
      const weekStart = getWeekStart();
      if (parsed.weekStart === weekStart) {
        setCompletedTasks(parsed.tasks || {});
      }
    }
  };

  const getWeekStart = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    return new Date(now.setDate(diff)).toISOString().split('T')[0];
  };

  const toggleTaskComplete = (day, taskIndex) => {
    const taskKey = `${day}-${taskIndex}`;
    setCompletedTasks(prev => {
      const updated = {
        ...prev,
        [taskKey]: !prev[taskKey]
      };
      
      // Save to localStorage
      localStorage.setItem('weekly_completed_tasks', JSON.stringify({
        weekStart: getWeekStart(),
        tasks: updated
      }));
      
      return updated;
    });
  };

  const getTasksForDay = (day) => {
    if (!weeklyPlan?.weekly_plan) return [];
    const dayPlan = weeklyPlan.weekly_plan.find(d => d.day === day);
    return dayPlan?.tasks || [];
  };

  const getDayStats = (day) => {
    const tasks = getTasksForDay(day);
    const completed = tasks.filter((_, idx) => completedTasks[`${day}-${idx}`]).length;
    return { completed, total: tasks.length };
  };

  const getTotalStats = () => {
    if (!weeklyPlan?.weekly_plan) return { completed: 0, total: 0, totalHours: 0 };
    
    let completed = 0;
    let total = 0;
    
    weeklyPlan.weekly_plan.forEach(day => {
      day.tasks.forEach((_, idx) => {
        total++;
        if (completedTasks[`${day.day}-${idx}`]) completed++;
      });
    });
    
    return {
      completed,
      total,
      totalHours: weeklyPlan.total_study_hours || 0,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-500">Loading your weekly plan...</p>
        </div>
      </div>
    );
  }

  const stats = getTotalStats();
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to="/roadmap">
              <Button data-testid="back-to-roadmap-btn" variant="ghost" size="icon" className="text-slate-500 hover:text-slate-900">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2 sm:gap-3">
                <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-500" />
                Weekly Planner
              </h1>
              <p className="text-slate-500 text-sm sm:text-base">
                Week of {weeklyPlan?.week_of} • Year {weeklyPlan?.year_track} Track
              </p>
            </div>
          </div>
          
          <div className="text-left sm:text-right ml-12 sm:ml-0">
            <p className="text-2xl sm:text-3xl font-bold text-emerald-600">{stats.percentage}%</p>
            <p className="text-slate-500 text-sm">{stats.completed}/{stats.total} tasks done</p>
          </div>
        </div>

        {/* Week Overview */}
        <Card className="bg-white border-slate-200 shadow-sm mb-6">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                <span className="text-slate-700">{stats.totalHours} hours planned</span>
              </div>
              {weeklyPlan?.weak_topics_focus?.length > 0 && (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  <span className="text-slate-600 text-sm sm:text-base">
                    Focus: {weeklyPlan.weak_topics_focus.slice(0, 2).join(', ')}
                    {weeklyPlan.weak_topics_focus.length > 2 && '...'}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Day Selector - Scrollable on mobile */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2 sm:grid sm:grid-cols-7 sm:overflow-visible">
          {days.map(day => {
            const dayStats = getDayStats(day);
            const isToday = day === getCurrentDay();
            const isSelected = day === selectedDay;
            
            return (
              <button
                key={day}
                data-testid={`day-selector-${day.toLowerCase()}`}
                onClick={() => setSelectedDay(day)}
                className={`flex-shrink-0 w-16 sm:w-auto p-3 rounded-lg text-center transition-all ${
                  isSelected 
                    ? 'bg-emerald-500 text-white' 
                    : isToday
                      ? 'bg-emerald-100 border border-emerald-300 text-emerald-600'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >
                <p className="font-medium text-sm">{day.slice(0, 3)}</p>
                <p className="text-xs mt-1 opacity-75">
                  {dayStats.completed}/{dayStats.total}
                </p>
                {dayStats.completed === dayStats.total && dayStats.total > 0 && (
                  <CheckCircle className="h-3 w-3 mx-auto mt-1" />
                )}
              </button>
            );
          })}
        </div>

        {/* Selected Day Tasks */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-emerald-500" />
              {selectedDay}&apos;s Tasks
              {selectedDay === getCurrentDay() && (
                <Badge className="bg-emerald-100 text-emerald-600 border-0 ml-2">Today</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {getTasksForDay(selectedDay).map((task, idx) => {
                const taskKey = `${selectedDay}-${idx}`;
                const isCompleted = completedTasks[taskKey];
                const config = TASK_TYPE_CONFIG[task.type] || TASK_TYPE_CONFIG.study;
                const Icon = config.icon;
                
                return (
                  <div
                    key={idx}
                    data-testid={`weekly-task-${selectedDay.toLowerCase()}-${idx}`}
                    onClick={() => toggleTaskComplete(selectedDay, idx)}
                    className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg cursor-pointer transition-all ${
                      isCompleted 
                        ? 'bg-emerald-50 border border-emerald-200' 
                        : 'bg-slate-50 hover:bg-white border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {/* Checkbox */}
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      isCompleted 
                        ? 'bg-emerald-500 border-emerald-500' 
                        : 'border-slate-300 hover:border-emerald-500'
                    }`}>
                      {isCompleted && <CheckCircle className="h-4 w-4 text-white" />}
                    </div>
                    
                    {/* Icon - Hidden on small screens */}
                    <div className={`hidden sm:flex w-10 h-10 rounded-lg items-center justify-center flex-shrink-0 bg-${config.color}-100`}>
                      <Icon className={`h-5 w-5 text-${config.color}-500`} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${isCompleted ? 'text-emerald-600 line-through' : 'text-slate-900'}`}>
                        {task.title}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
                          {config.label}
                        </Badge>
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {task.duration} min
                        </span>
                        {task.priority === 'high' && (
                          <Badge className="bg-red-100 text-red-600 border-0 text-xs">
                            Priority
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {getTasksForDay(selectedDay).length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No tasks scheduled for {selectedDay}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tips Section */}
        {weeklyPlan?.tips && (
          <Card className="bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200 mt-6">
            <CardHeader>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-yellow-500" />
                Study Tips
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {weeklyPlan.tips.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-slate-600">
                    <span className="text-emerald-500 mt-1">•</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default WeeklyPlannerPage;
